/**
 * PushTargetRenderer - Push node target and notification configuration
 *
 * Handles push notification target selection and message configuration.
 * Supports field mapping via FieldSourceToggle for title and body fields.
 */

import React from 'react';
import { Input } from '@/components/ui/Input';
import { FieldSourceToggle } from '../FieldSourceToggle';
import type { BaseCustomRendererProps } from './types';
import { configFieldKey } from './types';

export const PushTargetRenderer: React.FC<BaseCustomRendererProps> = ({
  nodeData,
  isDisabled = false,
  onBatchUpdate,
  fieldSourceHandlersFactory,
  availableSources = [],
  customInputs = [],
  contextVariables = [],
  startVariables = [],
}) => {
  const handleUpdate = (field: string, value: unknown) => {
    if (onBatchUpdate) {
      onBatchUpdate({ [field]: value });
    }
  };

  const pushTargetType = (nodeData.push_target_type as string) || 'follow_start';

  // Create field handlers for title and body
  const titleFieldKey = configFieldKey('title');
  const bodyFieldKey = configFieldKey('body');
  const titleHandlers = fieldSourceHandlersFactory?.createFieldHandlers(titleFieldKey);
  const bodyHandlers = fieldSourceHandlersFactory?.createFieldHandlers(bodyFieldKey);

  return (
    <div className="space-y-4">
      {/* Title */}
      <FieldSourceToggle
        fieldKey={titleFieldKey}
        label="通知标题"
        description="支持变量: {{变量名}} 引用输入字段"
        required={true}
        availableSources={availableSources}
        customInputs={customInputs}
        contextVariables={contextVariables}
        startVariables={startVariables}
        mapping={titleHandlers?.mapping}
        onMappingChange={titleHandlers?.onMappingChange || (() => {})}
        onKeyPathChange={titleHandlers?.onKeyPathChange || (() => {})}
        onMappingRemove={titleHandlers?.onMappingRemove || (() => {})}
        disabled={isDisabled}
        compatibleTypes={['string']}
      >
        <Input
          value={(nodeData.title as string) || ''}
          onChange={(e) => handleUpdate('title', e.target.value || undefined)}
          placeholder="通知标题"
          disabled={isDisabled}
        />
      </FieldSourceToggle>

      {/* Body */}
      <FieldSourceToggle
        fieldKey={bodyFieldKey}
        label="通知内容"
        description="支持变量: {{变量名}} 引用输入字段"
        required={true}
        availableSources={availableSources}
        customInputs={customInputs}
        contextVariables={contextVariables}
        startVariables={startVariables}
        mapping={bodyHandlers?.mapping}
        onMappingChange={bodyHandlers?.onMappingChange || (() => {})}
        onKeyPathChange={bodyHandlers?.onKeyPathChange || (() => {})}
        onMappingRemove={bodyHandlers?.onMappingRemove || (() => {})}
        disabled={isDisabled}
        compatibleTypes={['string']}
      >
        <textarea
          value={(nodeData.body as string) || ''}
          onChange={(e) => handleUpdate('body', e.target.value || undefined)}
          rows={2}
          disabled={isDisabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-rose-500 focus:border-rose-500 disabled:bg-gray-100"
          placeholder="通知内容..."
        />
      </FieldSourceToggle>

      {/* Target Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">推送目标</label>
        <div className="space-y-2">
          {/* Follow Start Config - uses same target_user as workflow start */}
          <label className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-rose-50 border border-rose-200 bg-rose-50/30">
            <input
              type="radio"
              name="push_target"
              checked={pushTargetType === 'follow_start'}
              onChange={() => handleUpdate('push_target_type', 'follow_start')}
              disabled={isDisabled}
              className="w-4 h-4 text-rose-600 border-gray-300 focus:ring-rose-500"
            />
            <div>
              <div className="text-sm font-medium text-rose-700">跟随启动配置</div>
              <p className="text-xs text-rose-500">使用启动项中的「目标用户」设置</p>
            </div>
          </label>

          <label className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-gray-50 border border-gray-200">
            <input
              type="radio"
              name="push_target"
              checked={pushTargetType === 'current_user'}
              onChange={() => handleUpdate('push_target_type', 'current_user')}
              disabled={isDisabled}
              className="w-4 h-4 text-rose-600 border-gray-300 focus:ring-rose-500"
            />
            <div>
              <div className="text-sm font-medium text-gray-700">当前用户</div>
              <p className="text-xs text-gray-500">推送给工作流执行时的登录用户</p>
            </div>
          </label>

          <label className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-gray-50 border border-gray-200">
            <input
              type="radio"
              name="push_target"
              checked={pushTargetType === 'all_users'}
              onChange={() => handleUpdate('push_target_type', 'all_users')}
              disabled={isDisabled}
              className="w-4 h-4 text-rose-600 border-gray-300 focus:ring-rose-500"
            />
            <div>
              <div className="text-sm font-medium text-gray-700">所有用户</div>
              <p className="text-xs text-gray-500">推送给所有注册了推送 Token 的用户</p>
            </div>
          </label>

          <label className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-gray-50 border border-gray-200">
            <input
              type="radio"
              name="push_target"
              checked={pushTargetType === 'specific_users'}
              onChange={() => handleUpdate('push_target_type', 'specific_users')}
              disabled={isDisabled}
              className="w-4 h-4 text-rose-600 border-gray-300 focus:ring-rose-500"
            />
            <div>
              <div className="text-sm font-medium text-gray-700">指定用户</div>
              <p className="text-xs text-gray-500">推送给指定的用户列表</p>
            </div>
          </label>
        </div>
      </div>

      {/* User IDs for specific_users mode */}
      {pushTargetType === 'specific_users' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            用户 ID 列表 <span className="text-red-500">*</span>
          </label>
          <textarea
            value={((nodeData.push_target_user_ids as string[]) || []).join('\n')}
            onChange={(e) => {
              const ids = e.target.value
                .split('\n')
                .map((id) => id.trim())
                .filter(Boolean);
              handleUpdate('push_target_user_ids', ids);
            }}
            rows={4}
            disabled={isDisabled}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-rose-500 focus:border-rose-500 disabled:bg-gray-100"
            placeholder="每行一个用户 ID&#10;例如:&#10;user_abc123&#10;user_xyz456"
          />
          <p className="text-xs text-gray-500 mt-1">每行一个用户ID，或映射数组类型变量</p>
        </div>
      )}

      {/* Sound Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">通知声音</label>
        <select
          value={(nodeData.push_sound as string) || 'default'}
          onChange={(e) => handleUpdate('push_sound', e.target.value)}
          disabled={isDisabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-rose-500 focus:border-rose-500 disabled:bg-gray-100"
        >
          <option value="default">默认</option>
          <option value="none">静音</option>
        </select>
      </div>

      {/* Badge Number */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">角标数字 (iOS)</label>
        <input
          type="number"
          min="0"
          value={(nodeData.push_badge as number) ?? ''}
          onChange={(e) =>
            handleUpdate('push_badge', e.target.value ? parseInt(e.target.value) : undefined)
          }
          disabled={isDisabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-rose-500 focus:border-rose-500 disabled:bg-gray-100"
          placeholder="留空不更新角标"
        />
      </div>
    </div>
  );
};

export default PushTargetRenderer;
